import Icon from '../common/Icon.jsx';
import { formatRaceDate } from '../../utils/time.js';

/**
 * RaceMeta — header summary for a selected race.
 *
 * Shows the meeting name, schedule, lap count, weather, pole, and fastest
 * lap. Data layout follows the OpenF1 nomenclature 1:1 so the live data
 * cutover is a no-op.
 */
export default function RaceMeta({ payload }) {
  return (
    <div className="relative mt-4 overflow-hidden rounded-2xl bg-bg-card ring-1 ring-stroke shadow-card">
      <div className="absolute inset-x-0 top-0 h-1 race-stripe opacity-90" />
      <div className="carbon-weave absolute inset-0 opacity-30" />

      <div className="relative px-5 pt-5 pb-4">
        <div className="text-2xs font-bold uppercase tracking-widest-plus text-f1-red">
          {payload.round ? `Round ${payload.round} · ` : ''}{payload.session_name}
        </div>
        <h2 className="mt-1 text-xl font-bold leading-tight text-ink-primary">
          {payload.meeting_name}
        </h2>
        <p className="mt-0.5 truncate text-2xs uppercase tracking-widest-plus text-ink-tertiary">
          {payload.meeting_official_name}
        </p>

        <div className="mt-3 flex items-center gap-2 text-xs text-ink-secondary">
          <Icon name="clock" size={14} className="text-ink-tertiary" />
          <span>{payload.date_start ? formatRaceDate(payload.date_start) : '-'}</span>
          <span className="text-ink-muted">·</span>
          <span className="font-mono tabular-nums">{payload.lap_count !== '-' ? `${payload.lap_count} laps` : ''}</span>
        </div>
      </div>

      <div className="relative grid grid-cols-3 gap-px border-t border-stroke bg-stroke">
        <MetaCell label="Pole">
          <div className="text-sm font-semibold text-ink-primary">
            {payload.pole_position.driver_number ? `#${payload.pole_position.driver_number}` : '-'}
          </div>
          <div className="truncate text-2xs text-ink-secondary">
            {abbrev(payload.pole_position.driver_name)}
          </div>
          <div className="font-mono text-2xs tabular-nums text-f1-red">
            {payload.pole_position.lap_time}
          </div>
        </MetaCell>

        <MetaCell label="Fastest lap">
          <div className="text-sm font-semibold text-ink-primary">
            {payload.fastest_lap.driver_number ? `#${payload.fastest_lap.driver_number}` : '-'}
          </div>
          <div className="truncate text-2xs text-ink-secondary">
            {abbrev(payload.fastest_lap.driver_name)}
          </div>
          <div className="font-mono text-2xs tabular-nums text-signal-purple">
            {payload.fastest_lap.lap_time}
          </div>
        </MetaCell>

        <MetaCell label={payload.weather.estimated ? 'Weather (est.)' : 'Weather'}>
          <div className="truncate text-sm font-semibold text-ink-primary">
            {payload.weather.conditions}
          </div>
          <div className="font-mono text-2xs tabular-nums text-ink-secondary">
            {payload.weather.air_temp_c !== '-' ? `Air ${payload.weather.air_temp_c}°C` : '-'}
          </div>
          <div className="font-mono text-2xs tabular-nums text-ink-tertiary">
            {payload.weather.track_temp_c !== '-'
              ? `Track ${payload.weather.estimated ? '~' : ''}${payload.weather.track_temp_c}°C`
              : ''}
          </div>
        </MetaCell>
      </div>
    </div>
  );
}

function MetaCell({ label, children }) {
  return (
    <div className="bg-bg-card px-3 py-3">
      <div className="mb-1 text-[10px] font-bold uppercase tracking-widest-plus text-ink-tertiary">
        {label}
      </div>
      {children}
    </div>
  );
}

/**
 * "Lewis Hamilton" → "L. Hamilton" — saves horizontal space in tight cells.
 */
function abbrev(fullName) {
  if (!fullName) return '';
  const parts = fullName.split(' ');
  if (parts.length < 2) return fullName;
  return `${parts[0][0]}. ${parts.slice(1).join(' ')}`;
}
